import { useState } from "react";
import { Button } from "./ui/button";
import { Star, X, Loader2, CheckCircle, Bike, Store } from "lucide-react";
import axios from "axios";
import { API } from "../utils/api";

const StarRating = ({ rating, setRating, label, icon: Icon }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center">
          <Icon size={18} className="text-[#3FA9F5]" />
        </div>
        <span className="font-medium text-gray-800">{label}</span>
      </div>
      <div className="flex gap-2 justify-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="transition-transform hover:scale-110 active:scale-95"
          >
            <Star
              size={36}
              className={`transition-colors ${
                star <= (hoverRating || rating)
                  ? "text-yellow-400 fill-yellow-400"
                  : "text-gray-300"
              }`}
            />
          </button>
        ))}
      </div>
      <p className="text-center text-sm text-gray-500 mt-2">
        {rating === 0 && "Tap to rate"}
        {rating === 1 && "Poor"}
        {rating === 2 && "Fair"}
        {rating === 3 && "Good"}
        {rating === 4 && "Very Good"}
        {rating === 5 && "Excellent!"}
      </p>
    </div>
  );
};

const RatingModal = ({ 
  isOpen, 
  onClose, 
  orderId, 
  orderType = "fleety", // "fleety" or "fleetygo"
  onRatingSubmitted 
}) => {
  const [riderRating, setRiderRating] = useState(0);
  const [riderFeedback, setRiderFeedback] = useState("");
  const [restaurantRating, setRestaurantRating] = useState(0);
  const [restaurantFeedback, setRestaurantFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (riderRating === 0) {
      setError("Please rate the rider");
      return;
    }

    if (orderType === "fleety" && restaurantRating === 0) {
      setError("Please rate the restaurant");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await axios.post(`${API}/customers/submit-rating`, {
        order_id: orderId,
        order_type: orderType,
        rider_rating: riderRating,
        rider_feedback: riderFeedback || null,
        restaurant_rating: orderType === "fleety" ? restaurantRating : null,
        restaurant_feedback: orderType === "fleety" ? restaurantFeedback || null : null
      });

      if (response.data.success) {
        setSubmitted(true);
        if (onRatingSubmitted) {
          setTimeout(() => {
            onRatingSubmitted();
          }, 2000);
        }
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit rating. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRiderRating(0);
      setRiderFeedback("");
      setRestaurantRating(0);
      setRestaurantFeedback("");
      setSubmitted(false);
      setError("");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden animate-slideUp max-h-[90vh] overflow-y-auto">
        {!submitted ? (
          <>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#3FA9F5] to-[#2196F3] p-6 text-center relative">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                data-testid="rating-close-btn"
              >
                <X size={24} />
              </button>
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                <CheckCircle size={32} className="text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {orderType === "fleety" ? "Order Delivered!" : "Package Delivered!"}
              </h2>
              <p className="text-white/90 text-sm mt-1">
                How was your experience?
              </p>
            </div>

            {/* Rating Content */}
            <div className="p-6">
              {/* Rider Rating */}
              <StarRating
                rating={riderRating}
                setRating={setRiderRating}
                label="Rate your Rider"
                icon={Bike}
              />
              <textarea
                value={riderFeedback}
                onChange={(e) => setRiderFeedback(e.target.value)}
                placeholder="Any feedback for the rider? (optional)"
                className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:border-[#3FA9F5] mb-6"
                rows={2}
                data-testid="rider-feedback-input"
              />

              {/* Restaurant Rating - Only for Fleety Food orders */}
              {orderType === "fleety" && (
                <>
                  <StarRating
                    rating={restaurantRating}
                    setRating={setRestaurantRating}
                    label="Rate the Restaurant"
                    icon={Store}
                  />
                  <textarea
                    value={restaurantFeedback}
                    onChange={(e) => setRestaurantFeedback(e.target.value)}
                    placeholder="Any feedback for the restaurant? (optional)"
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:border-[#3FA9F5] mb-6"
                    rows={2}
                    data-testid="restaurant-feedback-input"
                  />
                </>
              )}

              {/* Error Message */}
              {error && (
                <p className="text-red-500 text-sm text-center mb-4" data-testid="rating-error">
                  {error}
                </p>
              )}

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white font-semibold rounded-full transition-all disabled:opacity-50"
                data-testid="submit-rating-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Submit Rating"
                )}
              </Button>

              {/* Skip Option */}
              <button
                onClick={handleClose}
                className="w-full text-center text-gray-500 text-sm mt-3 hover:text-gray-700"
                data-testid="skip-rating-btn"
              >
                Skip for now
              </button>
            </div>
          </>
        ) : (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-6">
              Your feedback helps us improve our service.
            </p>
            <Button
              onClick={handleClose}
              className="w-full h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white font-semibold rounded-full"
              data-testid="rating-done-btn"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingModal;
